import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, LogOut, X } from "lucide-react";
import { http, errText } from "@/lib/api";
import { Logo } from "@/components/site/Primitives";

const Login = ({ onDone }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const r = await http.post("/auth/login", f); onDone(r.data); } catch (ex) { setErr(errText(ex, "Login failed")); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="card p-8 max-w-sm mx-auto space-y-4" data-testid="admin-login-form">
      <Logo className="w-10 h-10" />
      <h1 className="display text-3xl">Admin sign in</h1>
      <input data-testid="admin-email" className="field" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <input data-testid="admin-password" className="field" type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      {err && <p data-testid="admin-login-error" className="text-sm text-red-700">{err}</p>}
      <button data-testid="admin-login-submit" disabled={busy} className="pill-solid px-6 py-3 text-sm">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Sign in</button>
    </form>
  );
};

const Bookings = ({ rows, setStatus }) => (
  <div className="space-y-4" data-testid="admin-bookings">
    {rows.length === 0 && <p className="text-[color:var(--muted)]">No requests yet.</p>}
    {rows.map((b) => (
      <div key={b.id} className="card p-6 grid md:grid-cols-3 gap-4 text-sm" data-testid={`booking-${b.id}`}>
        <div>
          <p className="display text-2xl">{b.name}</p>
          <p><a className="underline" href={`tel:${b.phone}`}>{b.phone}</a>{b.email && <> · {b.email}</>}</p>
          <p className="text-xs text-[color:var(--muted)] mt-1">{new Date(b.created_at).toLocaleString()} · email to you {b.email_status}{b.email && <> · client email {b.client_email_status || "pending"}</>}</p>
        </div>
        <div className="text-[color:var(--muted)]">
          <p><b className="text-[color:var(--ink)]">Birth:</b> {b.birth_date} {b.birth_time || "(time unknown)"}, {b.birth_place}</p>
          <p><b className="text-[color:var(--ink)]">For:</b> {b.service} · {b.mode.replace("_", " ")}</p>
          <p><b className="text-[color:var(--ink)]">Slot:</b> {b.preferred_date} {b.preferred_time}</p>
          {b.questions && <p className="mt-2 whitespace-pre-line">{b.questions}</p>}
        </div>
        <div className="flex md:justify-end items-start gap-2">
          {["new", "contacted", "done"].map((s) => (
            <button key={s} data-testid={`booking-${b.id}-${s}`} onClick={() => setStatus(b.id, s)}
              className={`chip capitalize ${b.status === s ? "chip-on" : ""}`}>{s}</button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ChangePassword = () => {
  const [f, setF] = useState({ current: "", new: "", confirm: "" });
  const [msg, setMsg] = useState({ text: "", ok: false });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new !== f.confirm) { setMsg({ text: "New passwords do not match", ok: false }); return; }
    try {
      await http.post("/auth/change-password", { current: f.current, new: f.new });
      setMsg({ text: "Password changed.", ok: true }); setF({ current: "", new: "", confirm: "" });
    } catch (ex) { setMsg({ text: errText(ex, "Could not change password"), ok: false }); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="card p-6 max-w-md space-y-4" data-testid="change-password-form">
      <h2 className="display text-2xl">Change password</h2>
      <input data-testid="pw-current" type="password" className="field" placeholder="Current password" value={f.current} onChange={set("current")} required />
      <input data-testid="pw-new" type="password" className="field" placeholder="New password (min 8 characters)" value={f.new} onChange={set("new")} minLength={8} required />
      <input data-testid="pw-confirm" type="password" className="field" placeholder="Repeat new password" value={f.confirm} onChange={set("confirm")} required />
      {msg.text && <p data-testid="pw-msg" className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-700"}`}>{msg.text}</p>}
      <button data-testid="pw-submit" className="pill-solid px-6 py-3 text-sm">Update password</button>
    </form>
  );
};

const Stories = ({ rows, moderate }) => (
  <div className="space-y-4" data-testid="admin-stories">
    {rows.length === 0 && <p className="text-[color:var(--muted)]">No stories yet.</p>}
    {rows.map((s) => (
      <div key={s.id} className="card p-6 flex flex-col md:flex-row gap-4 justify-between text-sm" data-testid={`story-${s.id}`}>
        <div className="max-w-2xl">
          <p className="font-semibold">{s.name} · <span className="text-[color:var(--muted)] font-normal">{s.location}</span> <span className="chip !py-0.5 ml-2 capitalize">{s.status}</span></p>
          <p className="mt-2 leading-relaxed">{s.text}</p>
        </div>
        <div className="flex gap-2 items-start">
          <button data-testid={`story-${s.id}-approve`} onClick={() => moderate(s.id, "approved")} className="pill-ghost px-4 py-2 text-xs"><Check className="w-3.5 h-3.5 mr-1" />Approve</button>
          <button data-testid={`story-${s.id}-reject`} onClick={() => moderate(s.id, "rejected")} className="pill-ghost px-4 py-2 text-xs"><X className="w-3.5 h-3.5 mr-1" />Hide</button>
        </div>
      </div>
    ))}
  </div>
);

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [data, setData] = useState({ bookings: [], stories: [] });
  const load = useCallback(async () => {
    const [b, s] = await Promise.all([http.get("/admin/bookings"), http.get("/admin/stories")]);
    setData({ bookings: Array.isArray(b.data) ? b.data : [], stories: Array.isArray(s.data) ? s.data : [] });
  }, []);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);
  useEffect(() => { if (user) load().catch(() => setUser(null)); }, [user, load]);
  const setStatus = async (id, status) => { await http.patch(`/admin/bookings/${id}`, { status }); load(); };
  const moderate = async (id, status) => { await http.patch(`/admin/stories/${id}`, { status }); load(); };
  const logout = async () => { await http.post("/auth/logout"); setUser(null); };
  if (!checked) return <div className="min-h-screen" />;
  const pending = data.stories.filter((s) => s.status === "pending").length;
  return (
    <section className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-32 pb-24 min-h-screen" data-testid="admin-page">
      {!user ? <Login onDone={setUser} /> : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h1 className="display text-4xl">Namaste, Pandit ji</h1>
            <button data-testid="admin-logout" onClick={logout} className="pill-ghost px-5 py-2.5 text-sm"><LogOut className="w-4 h-4 mr-2" />Sign out</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            {[["bookings", `Requests (${data.bookings.length})`], ["stories", `Client stories (${pending} pending)`], ["account", "Account"]].map(([k, l]) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`chip ${tab === k ? "chip-on" : ""}`}>{l}</button>
            ))}
          </div>
          {tab === "bookings" && <Bookings rows={data.bookings} setStatus={setStatus} />}
          {tab === "stories" && <Stories rows={data.stories} moderate={moderate} />}
          {tab === "account" && <ChangePassword />}
        </>
      )}
    </section>
  );
}
